// File: apps/web/src/features/import/StagedList.tsx
// Version: 2026.4.7 | Sprint 7 Wave 3 — Staged conversations table
//
// Renders the paged list of staged conversations with status filters
// (Unmapped / Mapped / Committed / Ignored / All) and a row click that
// raises an event the parent uses to open the StagedDetail drawer.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { GlassPanel, SectionLabel, EmptyState, LoadingShimmer } from '../../components/engine/EnginePageShell';
import { Tooltip, TooltipTitle, TooltipBody, TooltipHint } from '../../components/engine/Tooltip';
import { useStagedConversations, useAcceptAllSuggestions, useAutoCommit, useCommitImport } from '../../lib/api/import';
import { useApiClient } from '@forgetrack/api-client';
import type {
  StagedConversationResponse,
  StagedStatus,
} from '../../lib/api/types-import';

interface StagedListProps {
  onRowClick:  (row: StagedConversationResponse) => void;
  batchFilter?: string;
  refreshKey?:  number;
}

// "NeedsReview" is a virtual filter — it's not a StagedStatus (mapping state),
// it's an orthogonal flag on top of the classification state. When selected,
// the request drops the mapping-status filter and instead asks the server for
// rows where ClassificationStatus='Classified' AND AIQualifiesForRDCredit IS
// NULL. That surfaces the null-on-suspicious safety-net queue regardless of
// whether the row is Unmapped, Mapped, or Committed.
type PrimaryFilter = StagedStatus | 'All' | 'NeedsReview';

interface FilterDef {
  value: PrimaryFilter;
  label: string;
  color: string;
  tooltip: React.ReactNode;
}

const FILTERS: FilterDef[] = [
  {
    value: 'Unmapped',
    label: 'Unmapped',
    color: C.amber,
    tooltip: (
      <>
        <TooltipTitle>Unmapped</TooltipTitle>
        <TooltipBody>
          Classified, but not yet assigned to a work item. This is the main
          review queue — accept the AI's suggested work item, pick a different
          one, or mark the row ignored.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'NeedsReview',
    label: 'Needs Review',
    color: C.coral,
    tooltip: (
      <>
        <TooltipTitle>Needs Review</TooltipTitle>
        <TooltipBody>
          Classifier returned zero evidence on a substantive session
          (&gt; 20 messages and &gt; 30 minutes). Almost always a model miss,
          not a real zero. Open each row, read the transcript, and set the
          R&amp;D verdict manually via the override button in the detail drawer.
        </TooltipBody>
        <TooltipHint kind="note">Skipped by reclassify — human owns these.</TooltipHint>
      </>
    ),
  },
  {
    value: 'Mapped',
    label: 'Mapped',
    color: C.cyan,
    tooltip: (
      <>
        <TooltipTitle>Mapped</TooltipTitle>
        <TooltipBody>
          Assigned to a work item but not yet committed. Can still be unmapped,
          re-assigned, or overridden. Nothing in the project actually changes
          until you auto-commit or commit a specific row.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'Committed',
    label: 'Committed',
    color: C.neon,
    tooltip: (
      <>
        <TooltipTitle>Committed</TooltipTitle>
        <TooltipBody>
          A real Session record exists in the project for this conversation.
          Effort, cost, AI usage, and R&amp;D evidence flow into dashboards
          and reports from here.
        </TooltipBody>
        <TooltipHint kind="irreversible">Removing a committed session is a manual DB operation.</TooltipHint>
      </>
    ),
  },
  {
    value: 'Ignored',
    label: 'Ignored',
    color: C.dim,
    tooltip: (
      <>
        <TooltipTitle>Ignored</TooltipTitle>
        <TooltipBody>
          Soft-deleted from the review queue. Not counted toward any project.
          Use for conversations that aren't worth mapping (chitchat, failed
          imports, test runs). Can be restored by opening the row.
        </TooltipBody>
      </>
    ),
  },
  {
    value: 'All',
    label: 'All',
    color: C.violet,
    tooltip: (
      <>
        <TooltipTitle>All rows</TooltipTitle>
        <TooltipBody>
          Every row in the batch regardless of status. Useful for a global
          overview; not typically used for day-to-day review.
        </TooltipBody>
      </>
    ),
  },
];

export function StagedList({ onRowClick, batchFilter, refreshKey }: StagedListProps) {
  const [statusFilter, setStatusFilter] = useState<PrimaryFilter>('Unmapped');
  const [page,         setPage]         = useState(1);
  const pageSize = 50;

  // Map the virtual primary filter to wire shape. NeedsReview sets the
  // needsReview query flag and clears the mapping-status filter so the
  // server returns rows across all mapping states.
  const request = {
    status:
      statusFilter === 'All' || statusFilter === 'NeedsReview'
        ? undefined
        : statusFilter,
    needsReview: statusFilter === 'NeedsReview' ? true : undefined,
    batchId:  batchFilter,
    page,
    pageSize,
  };

  const { data, loading, error, refetch } = useStagedConversations(request);

  // External refresh trigger (parent bumps refreshKey after a commit/ignore)
  // useEffect intentionally not used here because useStagedConversations
  // already re-runs when its request shape changes; the parent passes
  // refreshKey through the request via batchFilter or by updating state.
  // For an explicit refresh signal we expose refetch and let the parent
  // call it via useEffect, but we keep the prop here for interface clarity.
  void refreshKey;

  return (
    <GlassPanel accent={C.cyan}>
      <div style={{ padding: 24 }}>
        <div style={{
          display:        'flex',
          justifyContent: 'space-between',
          alignItems:     'center',
          marginBottom:   16,
        }}>
          <SectionLabel color={C.cyan}>STAGED CONVERSATIONS</SectionLabel>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ClassifyButton batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null} hasUnmapped={data?.items?.some(i => i.status === 'Unmapped') ?? false} onDone={refetch} />
            <AcceptAllButton
              batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null}
              hasClassified={data?.items?.some(i => i.classificationStatus === 'Classified' && i.status === 'Unmapped' && i.aiSuggestedWorkItemId != null) ?? false}
              onDone={refetch}
            />
            <AutoCommitButton
              batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null}
              hasClassified={data?.items?.some(i => i.classificationStatus === 'Classified' && i.status !== 'Committed' && i.status !== 'Ignored') ?? false}
              onDone={refetch}
            />
            <CommitAllMappedButton
              batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null}
              hasMapped={data?.items?.some(i => i.status === 'Mapped') ?? false}
              onDone={refetch}
            />
            <RetryFailedButton batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null} onDone={refetch} />
            <ReclassifyButton batchId={batchFilter ?? data?.items?.[0]?.sourceBatchId ?? null} onDone={refetch} />
            <Tooltip
              placement="bottom"
              maxWidth={240}
              content={
                <>
                  <TooltipTitle>Refresh</TooltipTitle>
                  <TooltipBody>
                    Re-fetch the staged conversation list. Use after a
                    classification job finishes to pull the latest verdicts
                    and status counts.
                  </TooltipBody>
                </>
              }
            >
              <button
                onClick={refetch}
                style={refreshButtonStyle}
                disabled={loading}
              >
                {loading ? '◌' : '↻'}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {FILTERS.map(f => (
            <Tooltip key={f.value} content={f.tooltip} placement="bottom" maxWidth={300}>
              <button
                onClick={() => { setStatusFilter(f.value); setPage(1); }}
                style={filterPillStyle(statusFilter === f.value, f.color)}
              >
                {f.label}
                {statusFilter === f.value && data && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>{data.total}</span>
                )}
              </button>
            </Tooltip>
          ))}
          {batchFilter && (
            <span style={batchPillStyle}>
              batch: <code style={{ fontFamily: F.mono, marginLeft: 4 }}>{batchFilter}</code>
            </span>
          )}
        </div>

        {/* Body */}
        {loading && !data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <LoadingShimmer height={48} />
            <LoadingShimmer height={48} />
            <LoadingShimmer height={48} />
            <LoadingShimmer height={48} />
          </div>
        )}

        {error && (
          <div style={errorBoxStyle}>
            Failed to load staged conversations: {error.message}
          </div>
        )}

        {data && data.items.length === 0 && (
          <EmptyState
            icon="📭"
            title={`No ${statusFilter === 'All' ? '' : statusFilter.toLowerCase() + ' '}conversations`}
            subtitle={statusFilter === 'Unmapped'
              ? 'Import a claude.ai export to get started.'
              : 'Try a different filter or import more conversations.'}
          />
        )}

        {data && data.items.length > 0 && (
          <>
            <div style={{
              border:       `1px solid ${C.faint}`,
              borderRadius: 8,
              overflow:     'hidden',
              background:   C.surface,
            }}>
              <div style={headerRowStyle}>
                <div style={{ flex: 3 }}>Title</div>
                <div style={{ flex: 2 }}>Project / Work Item</div>
                <div style={{ flex: 1, textAlign: 'right' }}>Cost</div>
                <div style={{ flex: 1, textAlign: 'right' }}>When</div>
              </div>

              {data.items.map(row => (
                <button
                  key={row.id}
                  onClick={() => onRowClick(row)}
                  style={dataRowStyle(row.status)}
                >
                  <div style={{ flex: 3, minWidth: 0 }}>
                    <div style={{
                      fontSize:      14,
                      fontWeight:    500,
                      color:         C.text,
                      whiteSpace:    'nowrap',
                      overflow:      'hidden',
                      textOverflow:  'ellipsis',
                      fontFamily:    F.display,
                    }}>
                      {row.title}
                    </div>
                    <div style={{ fontSize: 11, color: C.dim, marginTop: 2, fontFamily: F.mono }}>
                      {row.messageCount} msgs · {row.estimatedDurationMinutes}m active
                    </div>
                  </div>

                  <div style={{ flex: 2, minWidth: 0, fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {row.classificationConfidence != null && (
                        <span
                          style={{
                            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                            background: confidenceDotColor(row.classificationConfidence),
                            boxShadow: `0 0 5px ${confidenceDotColor(row.classificationConfidence)}`,
                          }}
                          title={`AI confidence: ${(row.classificationConfidence * 100).toFixed(0)}%`}
                        />
                      )}
                      {row.suggestedProjectName ? (
                        <span style={{ color: C.muted, fontFamily: F.display }}>
                          {row.suggestedProjectName}
                        </span>
                      ) : (
                        <span style={{ color: C.dim, fontFamily: F.display, fontStyle: 'italic' }}>
                          no project
                        </span>
                      )}
                    </div>
                    {row.mappedWorkItemTitle && (
                      <div style={{
                        color: C.cyan, fontSize: 11, fontFamily: F.display,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        → {row.mappedWorkItemTitle}
                      </div>
                    )}
                    {!row.mappedWorkItemTitle && row.aiSuggestedWorkItemTitle && (
                      <div style={{
                        color: C.violet, fontSize: 11, fontFamily: F.display,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        opacity: 0.7,
                      }}>
                        AI → {row.aiSuggestedWorkItemTitle}
                      </div>
                    )}
                  </div>

                  <div style={{
                    flex:       1,
                    textAlign:  'right',
                    fontFamily: F.mono,
                    fontSize:   12,
                    color:      row.estimatedCostUSD > 1 ? C.violet : C.muted,
                  }}>
                    ${row.estimatedCostUSD.toFixed(2)}
                  </div>

                  <div style={{
                    flex:       1,
                    textAlign:  'right',
                    fontSize:   11,
                    color:      C.dim,
                    fontFamily: F.mono,
                  }}>
                    {formatRelativeDate(row.sourceCreatedAt)}
                  </div>
                </button>
              ))}
            </div>

            {/* Pagination */}
            {data.total > pageSize && (
              <div style={paginationRowStyle}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={pageButtonStyle(page === 1)}
                >
                  ← Prev
                </button>
                <span style={{ color: C.muted, fontSize: 12, fontFamily: F.mono }}>
                  Page {page} of {Math.ceil(data.total / pageSize)} · {data.total} total
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * pageSize >= data.total}
                  style={pageButtonStyle(page * pageSize >= data.total)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </GlassPanel>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const filterPillStyle = (active: boolean, color: string): React.CSSProperties => ({
  padding:      '6px 14px',
  fontSize:     12,
  fontWeight:   600,
  fontFamily:   F.display,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color:        active ? color : C.muted,
  background:   active ? `${color}14` : 'transparent',
  border:       `1px solid ${active ? color + '58' : C.faint}`,
  borderRadius: 6,
  cursor:       'pointer',
  transition:   'all 0.15s ease',
});

const batchPillStyle: React.CSSProperties = {
  padding:      '6px 12px',
  fontSize:     11,
  color:        C.amber,
  background:   `${C.amber}08`,
  border:       `1px solid ${C.amber}28`,
  borderRadius: 6,
  fontFamily:   F.display,
};

const refreshButtonStyle: React.CSSProperties = {
  width:        32,
  height:       32,
  padding:      0,
  fontSize:     16,
  background:   'transparent',
  color:        C.muted,
  border:       `1px solid ${C.faint}`,
  borderRadius: 6,
  cursor:       'pointer',
};

const headerRowStyle: React.CSSProperties = {
  display:        'flex',
  gap:            16,
  padding:        '10px 16px',
  fontSize:       11,
  fontWeight:     600,
  letterSpacing:  '0.1em',
  textTransform:  'uppercase',
  color:          C.dim,
  background:     `${C.bg}88`,
  borderBottom:   `1px solid ${C.faint}`,
  fontFamily:     F.display,
};

const dataRowStyle = (status: StagedStatus): React.CSSProperties => ({
  display:      'flex',
  gap:          16,
  padding:      '12px 16px',
  textAlign:    'left',
  width:        '100%',
  background:   'transparent',
  border:       'none',
  borderBottom: `1px solid ${C.faint}`,
  borderLeft:   `3px solid ${statusBorderColor(status)}`,
  cursor:       'pointer',
  transition:   'background 0.12s ease',
  alignItems:   'center',
});

const errorBoxStyle: React.CSSProperties = {
  padding:      '16px',
  background:   `${C.coral}08`,
  border:       `1px solid ${C.coral}38`,
  borderRadius: 8,
  color:        C.coral,
  fontSize:     13,
  fontFamily:   F.display,
};

const paginationRowStyle: React.CSSProperties = {
  display:        'flex',
  justifyContent: 'space-between',
  alignItems:     'center',
  marginTop:      16,
};

const pageButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding:      '6px 14px',
  fontSize:     12,
  background:   'transparent',
  color:        disabled ? C.dim : C.cyan,
  border:       `1px solid ${disabled ? C.faint : C.cyan + '58'}`,
  borderRadius: 6,
  cursor:       disabled ? 'not-allowed' : 'pointer',
  fontFamily:   F.display,
});

function statusBorderColor(status: StagedStatus): string {
  switch (status) {
    case 'Mapped':    return C.cyan;
    case 'Committed': return C.neon;
    case 'Ignored':   return C.dim;
    default:          return C.amber;
  }
}

function confidenceDotColor(confidence: number): string {
  if (confidence >= 0.85) return C.neon;
  if (confidence >= 0.6)  return C.amber;
  return C.coral;
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1)  return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7)  return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Accept All AI Suggestions Button ─────────────────────────────────────────

function AcceptAllButton({ batchId, hasClassified, onDone }: { batchId: string | null; hasClassified: boolean; onDone: () => void }) {
  const { acceptAll, loading } = useAcceptAllSuggestions();
  const [result, setResult] = useState<string | null>(null);

  if (!hasClassified) return null;

  const handleAcceptAll = async () => {
    setResult(null);
    const res = await acceptAll({ batchId: batchId ?? undefined, minConfidence: 0.7 });
    if (res) {
      setResult(`✓ ${res.accepted} accepted (≥${(res.threshold * 100).toFixed(0)}% confidence)`);
      onDone();
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip
        placement="bottom"
        maxWidth={340}
        content={
          <>
            <TooltipTitle>✓ Accept All AI</TooltipTitle>
            <TooltipBody>
              Map every Unmapped row to its AI-suggested work item, but only
              where AI confidence is at least 70%. Low-confidence suggestions
              and rows with no suggestion are skipped — you review those
              manually. Status flow: Unmapped → Mapped.
            </TooltipBody>
            <TooltipHint kind="reversible">
              Each row can still be unmapped or re-assigned before commit.
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={handleAcceptAll}
          disabled={loading}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: loading ? C.faint : `${C.neon}15`,
            color: loading ? C.dim : C.neon,
            border: `1px solid ${loading ? C.faint : C.neon + '40'}`,
          }}
        >
          {loading ? '◌ Accepting...' : '✓ Accept All AI'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}
    </div>
  );
}

// ── Classify Button ─────────────────────────────────────────────────────────

function ClassifyButton({ batchId, hasUnmapped, onDone }: { batchId: string | null; hasUnmapped: boolean; onDone: () => void }) {
  const { createApi } = useApiClient();
  const [classifying, setClassifying] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Only show if there's a batch and pending conversations
  if (!batchId || !hasUnmapped) return null;

  const handleClassify = async () => {
    setClassifying(true);
    setResult(null);
    try {
      const api = await createApi();
      const res = await api.post<{ classified: number; failed: number; totalRows: number }>(
        '/api/v1/import/classify', { batchId });
      setResult(`✓ ${res.data.classified} classified, ${res.data.failed} failed`);
      onDone();
    } catch (err: any) {
      setResult(`✗ ${err?.response?.data?.error ?? 'Failed'}`);
    } finally {
      setClassifying(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip
        placement="bottom"
        maxWidth={340}
        content={
          <>
            <TooltipTitle>🔬 Classify</TooltipTitle>
            <TooltipBody>
              Run the AI classification pipeline on every Pending row in this
              batch. Pipeline: triage → R&amp;D evidence extraction →
              decision capture → complexity scoring. Each row is billed for
              tokens. Status flow: Pending → Classifying → Classified (or
              Failed on error).
            </TooltipBody>
            <TooltipHint kind="cost">
              Per-row token cost charged to your tenant's AI budget.
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={handleClassify}
          disabled={classifying}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: classifying ? C.faint : `${C.violet}15`,
            color: classifying ? C.dim : C.violet,
            border: `1px solid ${classifying ? C.faint : C.violet + '40'}`,
          }}
        >
          {classifying ? '◌ Classifying...' : '🔬 Classify'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}
    </div>
  );
}

// ── Auto-Commit All Button ──────────────────────────────────────────────────

function AutoCommitButton({ batchId, hasClassified, onDone }: { batchId: string | null; hasClassified: boolean; onDone: () => void }) {
  const { autoCommit, loading } = useAutoCommit();
  const [result, setResult] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!hasClassified) return null;

  const handleClick = async () => {
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 5000);
      return;
    }
    setConfirming(false);
    setResult(null);
    const res = await autoCommit({ batchId: batchId ?? undefined });
    if (res) {
      const parts = [];
      if (res.committed > 0) parts.push(`${res.committed} committed`);
      if (res.workItemsCreated > 0) parts.push(`${res.workItemsCreated} work items created`);
      if (res.phasesCreated > 0) parts.push(`${res.phasesCreated} phases created`);
      if (res.skipped > 0) parts.push(`${res.skipped} skipped (no project)`);
      if (res.failed > 0) parts.push(`${res.failed} failed`);
      setResult(`✓ ${parts.join(', ')}`);
      onDone();
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip
        placement="bottom"
        maxWidth={360}
        content={
          <>
            <TooltipTitle>⚡ Auto-commit All</TooltipTitle>
            <TooltipBody>
              Create real Session records for every Mapped row. Will
              auto-create missing work items and phases if the AI suggested
              ones that don't exist in your project yet. Do this LAST, after
              the needs-review queue is cleared and any low-confidence
              mappings have been reviewed.
            </TooltipBody>
            <TooltipHint kind="irreversible">
              Creates permanent Session entries. Requires a second click to confirm.
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={handleClick}
          disabled={loading}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: loading ? C.faint : confirming ? C.neon : `${C.cyan}15`,
            color: loading ? C.dim : confirming ? '#050910' : C.cyan,
            border: `1px solid ${loading ? C.faint : confirming ? C.neon : C.cyan + '40'}`,
            boxShadow: confirming ? `0 0 12px ${C.neonGlow}` : 'none',
          }}
        >
          {loading ? '◌ Committing...' : confirming ? 'Click again to confirm' : '⚡ Auto-commit All'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}
    </div>
  );
}

// ── Commit All Mapped Button ────────────────────────────────────────────────
// Distinct from Auto-commit: only commits rows already in Mapped state. Does
// not create work items / phases — operates strictly on existing mappings.
// Visible whenever the current view has at least one Mapped row.

function CommitAllMappedButton({
  batchId, hasMapped, onDone,
}: { batchId: string | null; hasMapped: boolean; onDone: () => void }) {
  const { commit, loading } = useCommitImport();
  const [result, setResult] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!hasMapped) return null;

  const handleClick = async () => {
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 5000);
      return;
    }
    setConfirming(false);
    setResult(null);
    const res = await commit({ allMapped: true, batchId: batchId ?? undefined });
    if (res) {
      const parts: string[] = [];
      if (res.succeeded > 0) parts.push(`${res.succeeded} committed`);
      if (res.failed > 0) parts.push(`${res.failed} failed`);
      setResult(`✓ ${parts.join(', ') || `0 / ${res.total}`}`);
      onDone();
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip
        placement="bottom"
        maxWidth={360}
        content={
          <>
            <TooltipTitle>✓ Commit All Mapped</TooltipTitle>
            <TooltipBody>
              Commit every row currently in Mapped state — turns the staged
              mappings into permanent Session records. Does NOT create new
              work items or phases (use Auto-commit for that). Use this when
              you've manually reviewed + accepted mappings and want to push
              them through in one click.
            </TooltipBody>
            <TooltipHint kind="irreversible">
              Creates permanent Session entries. Requires a second click to confirm.
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={handleClick}
          disabled={loading}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: loading ? C.faint : confirming ? C.neon : `${C.neon}15`,
            color: loading ? C.dim : confirming ? '#050910' : C.neon,
            border: `1px solid ${loading ? C.faint : confirming ? C.neon : C.neon + '40'}`,
            boxShadow: confirming ? `0 0 12px ${C.neonGlow}` : 'none',
          }}
        >
          {loading ? '◌ Committing...' : confirming ? 'Click again to confirm' : '✓ Commit All Mapped'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}
    </div>
  );
}

// ── Retry Failed Button ─────────────────────────────────────────────────────

function RetryFailedButton({ batchId, onDone }: { batchId: string | null; onDone: () => void }) {
  const { createApi } = useApiClient();
  const [retrying, setRetrying] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  if (!batchId) return null;

  const handleRetry = async () => {
    setRetrying(true);
    setResult(null);
    try {
      const api = await createApi();
      const res = await api.post<{ reset: number; classified: number; failed: number; totalRows: number; message?: string }>(
        '/api/v1/import/retry-failed', { batchId });
      if (res.data.reset === 0) {
        setResult('No failed rows to retry');
      } else {
        setResult(`✓ Reset ${res.data.reset}, classified ${res.data.classified}, failed ${res.data.failed}`);
      }
      onDone();
    } catch (err: any) {
      setResult(`✗ ${err?.response?.data?.error ?? 'Failed'}`);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tooltip
        placement="bottom"
        maxWidth={340}
        content={
          <>
            <TooltipTitle>↻ Retry Failed</TooltipTitle>
            <TooltipBody>
              Reset rows stuck in Failed or Classifying state back to Pending
              and re-run classification. Use after a transient error — timeout,
              auth hiccup, rate limit, mid-batch restart. Does not touch rows
              that classified successfully.
            </TooltipBody>
            <TooltipHint kind="cost">
              Each retried row is billed for a fresh classification call.
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={handleRetry}
          disabled={retrying}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: retrying ? C.faint : `${C.coral}15`,
            color: retrying ? C.dim : C.coral,
            border: `1px solid ${retrying ? C.faint : C.coral + '40'}`,
          }}
        >
          {retrying ? '◌ Retrying...' : '↻ Retry Failed'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}
    </div>
  );
}

// ─── Reclassify Button ──────────────────────────────────────────────────────
//
// Resets Classified rows back to Pending and re-runs the classification
// pipeline against whatever prompt is currently active. Two scopes:
//   • not-rd-qualified — only rows the prompt rejected as not-R&D. Use after
//     a prompt tweak aimed at reducing false negatives.
//   • all              — every Classified row in the batch. Use after a
//     substantive prompt rewrite when any row could flip direction.
// The endpoint returns 202 + a Hangfire job id; progress streams via the
// ImportHub SignalR group, same as the original import flow.
function ReclassifyButton({ batchId, onDone }: { batchId: string | null; onDone: () => void }) {
  const { createApi } = useApiClient();
  const [scope, setScope]     = useState<'not-rd-qualified' | 'null-verdict' | 'not-qualifying' | 'all'>('not-rd-qualified');
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!batchId) return null;

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const api = await createApi();
      const res = await api.post<{ reset: number; jobId?: string; message?: string }>(
        '/api/v1/import/reclassify', { batchId, scope });
      if (res.data.reset === 0) {
        setResult(res.data.message ?? 'No matching rows');
      } else {
        setResult(`✓ Queued ${res.data.reset} row${res.data.reset === 1 ? '' : 's'} · job ${res.data.jobId?.slice(0, 8) ?? '—'}`);
      }
      onDone();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err instanceof Error ? err.message : 'Reclassify failed');
      setResult(`✗ ${msg}`);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
      <Tooltip
        placement="bottom"
        maxWidth={360}
        content={
          <>
            <TooltipTitle>Reclassify scope</TooltipTitle>
            <TooltipBody>
              <div style={{ marginBottom: 6 }}>
                <strong style={{ color: C.text }}>not-R&amp;D only</strong> —
                resets rows where the AI explicitly decided NOT R&amp;D
                (AIQualifiesForRDCredit = 0). Excludes Needs Review rows
                (those are owned by a human). Use this after a prompt
                improvement aimed at reducing false negatives — fast and
                cheap.
              </div>
              <div>
                <strong style={{ color: C.text }}>all classified</strong> —
                resets every Classified row in the batch regardless of
                verdict. Use after a substantive prompt rewrite when any
                row could flip direction. Much more expensive.
              </div>
            </TooltipBody>
          </>
        }
      >
        <select
          value={scope}
          onChange={e => setScope(e.target.value as 'not-rd-qualified' | 'null-verdict' | 'not-qualifying' | 'all')}
          disabled={busy}
          style={{
            padding: '5px 10px', borderRadius: 6, fontSize: 11,
            fontFamily: F.mono, cursor: busy ? 'not-allowed' : 'pointer',
            background: `${C.violet}10`, color: C.violet,
            border: `1px solid ${C.violet}40`, outline: 'none',
          }}
        >
          <option value="not-rd-qualified">scope: not-R&amp;D (false only)</option>
          <option value="null-verdict">scope: null verdicts (needs-review)</option>
          <option value="not-qualifying">scope: not-R&amp;D + null verdicts</option>
          <option value="all">scope: all classified</option>
        </select>
      </Tooltip>
      <Tooltip
        placement="bottom"
        maxWidth={340}
        content={
          <>
            <TooltipTitle>⟲ Reclassify</TooltipTitle>
            <TooltipBody>
              {scope === 'all'
                ? 'Reset EVERY Classified row in this batch back to Pending and re-run the pipeline against the currently active prompt. You will pay fresh token cost for each row. Progress streams via SignalR.'
                : scope === 'null-verdict'
                ? 'Reset rows where Tier 4 returned a NULL verdict — typically the null-on-suspicious safety net firing on long substantive sessions. Highest-value retest population after a windowing or prompt change.'
                : scope === 'not-qualifying'
                ? 'Reset every non-yes row (false verdicts AND null verdicts). Full coverage of non-qualifying rows; costs more than the targeted scopes.'
                : 'Reset only rows marked NOT R&D (false verdicts only) and re-run the pipeline. Null/needs-review rows are left alone — pick the null-verdict scope for those.'}
            </TooltipBody>
            <TooltipHint kind="cost">
              {scope === 'all'
                ? 'Every classified row is billed again — expect meaningful cost.'
                : 'Only not-R&D rows are re-billed — usually a fraction of "all" cost.'}
            </TooltipHint>
          </>
        }
      >
        <button
          onClick={() => setConfirming(true)}
          disabled={busy}
          style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 11,
            fontWeight: 600, fontFamily: F.mono, cursor: 'pointer',
            background: busy ? C.faint : `${C.violet}15`,
            color: busy ? C.dim : C.violet,
            border: `1px solid ${busy ? C.faint : C.violet + '40'}`,
          }}
        >
          {busy ? '◌ Queuing…' : '⟲ Reclassify'}
        </button>
      </Tooltip>
      {result && <span style={{ fontSize: 10, color: C.muted, fontFamily: F.mono }}>{result}</span>}

      {/* Modal rendered via React Portal at document.body. The page-level
          glass panels use CSS filters / transforms for the "engine" look,
          which creates new stacking contexts and re-parents position:fixed
          to the panel instead of the viewport — that's why the modal
          appeared mid-page and required scrolling. Portaling bypasses every
          ancestor stacking context so the overlay truly fills the viewport. */}
      {confirming && !busy && createPortal(
        <div
          onClick={() => setConfirming(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.panel, border: `1px solid ${C.faint}`, borderRadius: 10,
              padding: 22, width: 440, maxWidth: '90vw',
              boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
            }}
          >
            <h3 style={{ margin: 0, fontFamily: F.display, fontSize: 16, color: C.text }}>
              Reclassify {scope === 'all' ? 'ALL Classified' : 'Not-R&D'} rows?
            </h3>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginTop: 10 }}>
              Batch: <code style={{ fontFamily: F.mono, color: C.cyan }}>{batchId}</code>
              <br /><br />
              Matching rows will flip back to <strong style={{ color: C.amber }}>Pending</strong> and
              be re-classified against the currently active prompt. You'll pay fresh token cost for
              each row. Progress streams via SignalR.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setConfirming(false)}
                style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12,
                  fontFamily: F.mono, background: 'transparent', cursor: 'pointer',
                  border: `1px solid ${C.faint}`, color: C.muted,
                }}
              >
                Cancel
              </button>
              <button
                onClick={run}
                style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12,
                  fontFamily: F.mono, background: `${C.violet}25`, cursor: 'pointer',
                  border: `1px solid ${C.violet}`, color: C.violet,
                }}
              >
                Reclassify
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
